/**
 * SEO utilities and configuration for Cryo by Breum
 * 
 * Strict SERP Pixel Constraints:
 * - Meta Title: Max 512 px (displayed in Arial 20px)
 * - Meta Description: Max 970 px (displayed in Arial 14px)
 */

export const MAX_TITLE_PX = 512;
export const MAX_DESCRIPTION_PX = 970;

export const FONT_TITLE = '20px Arial, sans-serif';
export const FONT_DESCRIPTION = '14px Arial, sans-serif';

const DEFAULT_IMAGE = 'https://assets.auxodev.dk/assets/bbc8c269-a12e-46e2-947b-0cefa0025eea/cryo-by-breum-reception-og-ventevaerelse.jpg';

/**
 * Accurately measures the rendered pixel width of a string.
 * Uses an in-memory canvas when running in browser.
 */
let cachedCanvas = null;
export function measureTextPx(text, font = FONT_TITLE) {
  if (!text) return 0;
  if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    try {
      if (!cachedCanvas) {
        cachedCanvas = document.createElement('canvas');
      }
      const ctx = cachedCanvas.getContext('2d');
      if (ctx) {
        ctx.font = font;
        return ctx.measureText(text).width;
      }
    } catch {
      // Fallback below
    }
  }

  // Fallback estimation based on average Arial character metrics
  const isTitle = font.includes('20px');
  const avgCharPx = isTitle ? 9.8 : 6.9;
  return text.length * avgCharPx;
}

/**
 * Trims text at word boundaries so that rendered width stays strictly <= maxPx
 */
export function clampToPixelWidth(text, maxPx, font = FONT_TITLE) {
  if (!text) return '';
  const initialWidth = measureTextPx(text, font);
  if (initialWidth <= maxPx) return text;

  let truncated = text.trim();
  while (truncated.length > 0 && measureTextPx(`${truncated}...`, font) > maxPx) {
    const lastSpace = truncated.lastIndexOf(' ');
    if (lastSpace === -1) {
      truncated = truncated.slice(0, -1);
    } else {
      truncated = truncated.slice(0, lastSpace);
    }
  }

  return truncated ? `${truncated}...` : text.slice(0, 20);
}

/**
 * Standard verified SEO metadata per page.
 * All titles are verified <= 512 px (Arial 20px).
 * All descriptions are verified <= 970 px (Arial 14px).
 */
export const PAGE_SEO = {
  home: {
    title: 'Eksklusive kropsbehandlinger | Cryo by Breum',
    description: 'Klinik i Odense for fedtfrysning, EMS træning og Body Roll. Få et personligt forløb med fokus på din krop og velvære. Book tid hos Cryo by Breum.',
    image: 'https://assets.auxodev.dk/assets/bbc8c269-a12e-46e2-947b-0cefa0025eea/cryo-by-breum-reception-og-ventevaerelse.jpg',
    path: '/',
  },
  fedtfrysning: {
    title: 'Fedtfrysning i Odense | Cryo by Breum',
    description: 'Effektiv og skånsom fedtfrysning i Odense SV hos Cryo by Breum. Målrettet kuldebehandling med godkendt udstyr i trygge rammer. Book tid online.',
    image: 'https://assets.auxodev.dk/assets/bbc8c269-a12e-46e2-947b-0cefa0025eea/cryo-by-breum-fedtfrysning-behandling-applikatorer.jpg',
    path: '/behandlinger/fedtfrysning',
  },
  'ems-traening': {
    title: 'EMS Træning i Odense | Cryo by Breum',
    description: 'Prøv EMS træning i Odense hos Cryo by Breum. Effektiv og skånsom helkropstræning på kun 25 minutter med personlig instruktør. Book en prøvetime.',
    image: 'https://assets.auxodev.dk/assets/bbc8c269-a12e-46e2-947b-0cefa0025eea/cryo-by-breum-ems-studio-behandlingsrum.jpg',
    path: '/behandlinger/ems-traening',
  },
  'body-roll': {
    title: 'Body Roll i Odense | Cryo by Breum',
    description: 'Oplev Body Roll i Odense hos Cryo by Breum. Dybdegående rullemassage med infrarød varme, der stimulerer lymfesystemet og øger velvære. Book tid.',
    image: 'https://assets.auxodev.dk/assets/bbc8c269-a12e-46e2-947b-0cefa0025eea/cryo-by-breum-body-roll-massage-maskine.jpg',
    path: '/behandlinger/body-roll',
  },
  prices: {
    title: 'Priser | Cryo by Breum i Odense',
    description: 'Se priser på fedtfrysning, EMS træning og Body Roll hos Cryo by Breum i Odense. Find enkeltture, klippekort og forløb. Book din behandling her.',
    image: DEFAULT_IMAGE,
    path: '/priser',
  },
  about: {
    title: 'Om Cryo by Breum | Klinik i Odense SV',
    description: 'Læs om Cryo by Breum på Dalumvej i Odense SV. Vi tilbyder personlige forløb inden for fedtfrysning, EMS og Body Roll i rolige, feminine rammer.',
    image: 'https://assets.auxodev.dk/assets/bbc8c269-a12e-46e2-947b-0cefa0025eea/cryo-by-breum-klinik-behandlingsrum-briks.jpg',
    path: '/om-cryo-by-breum',
  },
  contact: {
    title: 'Kontakt Cryo by Breum | Odense SV',
    description: 'Find kontaktinfo og åbningstider for Cryo by Breum på Dalumvej 82 i Odense SV. Ring, send en besked eller book din tid direkte via Planway.',
    image: DEFAULT_IMAGE,
    path: '/kontakt',
  },
  notFound: {
    title: '404 Siden findes ikke | Cryo by Breum',
    description: 'Den efterspurgte side kunne ikke findes. Gå til Cryo by Breum forsiden for at se vores behandlinger i Odense eller book en tid online.',
    image: DEFAULT_IMAGE,
    robots: 'noindex, nofollow',
  },
  forbidden: {
    title: '403 Adgang nægtet | Cryo by Breum',
    description: 'Adgang nægtet. Du har ikke tilladelse til denne side. Vend tilbage til forsiden for at udforske behandlinger hos Cryo by Breum i Odense.',
    image: DEFAULT_IMAGE,
    robots: 'noindex, nofollow',
  },
};

/**
 * Updates DOM head tags directly with strict length checking.
 */
export function applyHeadMeta({
  title,
  description,
  image = DEFAULT_IMAGE,
  path = '',
  robots = 'index, follow',
}) {
  if (typeof document === 'undefined') return;

  // Enforce pixel limits
  const safeTitle = clampToPixelWidth(title, MAX_TITLE_PX, FONT_TITLE);
  const safeDescription = clampToPixelWidth(description, MAX_DESCRIPTION_PX, FONT_DESCRIPTION);

  // 1. Document Title
  document.title = safeTitle;

  // 2. Helper to set/update meta tag
  const setMetaTag = (attribute, value, content) => {
    let el = document.querySelector(`meta[${attribute}="${value}"]`);
    if (!el) {
      el = document.createElement('meta');
      el.setAttribute(attribute, value);
      document.head.appendChild(el);
    }
    el.setAttribute('content', content || '');
  };

  // 3. Helper to set/update link tag
  const setLinkTag = (rel, href) => {
    let el = document.querySelector(`link[rel="${rel}"]`);
    if (!el) {
      el = document.createElement('link');
      el.setAttribute('rel', rel);
      document.head.appendChild(el);
    }
    el.setAttribute('href', href || '');
  };

  // Standard Meta
  setMetaTag('name', 'description', safeDescription);
  setMetaTag('name', 'robots', robots);

  // Open Graph
  setMetaTag('property', 'og:title', safeTitle);
  setMetaTag('property', 'og:description', safeDescription);
  setMetaTag('property', 'og:image', image);
  setMetaTag('property', 'og:type', 'website');
  setMetaTag('property', 'og:locale', 'da_DK');
  setMetaTag('property', 'og:site_name', 'Cryo by Breum');

  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://cryobybreum.dk';
  const canonicalUrl = path ? `${origin}${path.startsWith('/') ? path : `/${path}`}` : window.location.href;
  setMetaTag('property', 'og:url', canonicalUrl);
  setLinkTag('canonical', canonicalUrl);

  // Twitter Card
  setMetaTag('name', 'twitter:card', 'summary_large_image');
  setMetaTag('name', 'twitter:title', safeTitle);
  setMetaTag('name', 'twitter:description', safeDescription);
  setMetaTag('name', 'twitter:image', image);
}
